import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

import { Col, Select } from "@administrate/piston-ux";

import { useFormContext } from "@administrate/piston-ux/lib/Form";
import { useTranslation } from "react-i18next";
import { PointOfSaleLearnerFieldDefinition as LMSPointOfSaleLearnerFieldDefinition } from "../generated/lmsTypes";
import {
  Country,
  PointOfSaleLearnerFieldDefinition as WebLinkPointOfSaleLearnerFieldDefinition,
} from "../generated/weblinkTypes";
import { useWebLinkQuery } from "../hooks/weblink";
import { COUNTRY_QUERY } from "../queries/country";

import { TFunction } from "react-i18next";

type PosFieldDef =
  | WebLinkPointOfSaleLearnerFieldDefinition
  | LMSPointOfSaleLearnerFieldDefinition;
type Option = {
  label: string;
  value: string;
};

type CountryProvinceSelectorProps = {
  contactId: string | undefined;
  name: string;
  definition: PosFieldDef;
  required: boolean;
  onChange?: (newValue: string) => void;
};

const PROVINCE_COUNTRY_INPUT_NAME = "province-country";

const getCountryCodeFromProvinceValue = (
  provinceValue: string,
): string | null => {
  if (!provinceValue) return null;
  if (provinceValue.includes("-")) return provinceValue.split("-")[0];
  return provinceValue;
};

const CountryProvinceSelector = ({
  contactId,
  name,
  definition,
  required = false,
  onChange = () => {},
}: CountryProvinceSelectorProps): JSX.Element => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const { values } = useFormContext();

  // When the contact changes reset the initial values
  useEffect(() => {
    const provinceCode = values.get(name);
    const countryCode = getCountryCodeFromProvinceValue(provinceCode);
    if (countryCode) {
      setSelectedCountry(countryCode);
      values.set(PROVINCE_COUNTRY_INPUT_NAME, countryCode);
    }
  }, [contactId, name, values, setSelectedCountry]);

  const globalProvinceOptions: Record<string, Option[]> = _.groupBy(
    definition.options || [],
    x => getCountryCodeFromProvinceValue(x.value),
  );

  const { data: dataCountries, loading: loadingCountries } = useWebLinkQuery(
    COUNTRY_QUERY,
    { fetchPolicy: "cache-first" },
  );
  const countryOptions: Option[] = useMemo(() => {
    if (loadingCountries || !dataCountries) return [];

    return dataCountries.countries.map((country: Country) => ({
      label: country.name,
      value: country.code,
    }));
  }, [dataCountries, loadingCountries]);

  return (
    <>
      <CountrySelector
        name={PROVINCE_COUNTRY_INPUT_NAME}
        definition={definition}
        onChange={onChange}
        countryOptions={countryOptions}
        loadingCountries={loadingCountries}
        setSelectedCountry={setSelectedCountry}
        required={required}
        t={t}
      />
      <ProvinceSelector
        name={name}
        definition={definition}
        loadingCountries={loadingCountries}
        selectedCountry={selectedCountry}
        globalProvinceOptions={globalProvinceOptions}
        onChange={onChange}
        required={required}
        t={t}
      />
    </>
  );
};

type CountrySelectorProps = {
  name: string;
  definition: PosFieldDef;
  onChange: (newValue: string) => void;
  required: boolean;
  countryOptions: Option[];
  loadingCountries: boolean;
  setSelectedCountry: (newValue: string) => void;
  t: TFunction;
};

const CountrySelector = ({
  name,
  definition,
  onChange = () => {},
  required = false,
  countryOptions,
  loadingCountries,
  setSelectedCountry,
  t,
}: CountrySelectorProps) => {
  const countryValid = (value: string): boolean => !required || value !== null;

  const onCountryChange = (newCountryCode: string) => {
    setSelectedCountry(newCountryCode);
    onChange(newCountryCode);
  };

  return (
    <Col sm={6}>
      <Select
        name={name}
        label={t("Country")}
        key={`${definition.key} - Country`}
        onChange={onCountryChange}
        options={_.sortBy(countryOptions, "label")}
        loading={loadingCountries}
        valid={countryValid}
        required={required}
      />
    </Col>
  );
};

type ProvinceSelectorProps = {
  name: string;
  definition: PosFieldDef;
  required: boolean;
  onChange: (newValue: string) => void;
  loadingCountries: boolean;
  selectedCountry: string | null;
  globalProvinceOptions: Record<string, Option[]>;
  t: TFunction;
};

const ProvinceSelector = ({
  name,
  definition,
  required = false,
  onChange = () => {},
  loadingCountries,
  selectedCountry,
  globalProvinceOptions,
  t,
}: ProvinceSelectorProps) => {
  const getProvinceOptions = (country: string | null) => {
    if (!country) return [];
    if (_.isEmpty(country)) return [];
    return globalProvinceOptions[country];
  };

  const isProvinceValueValid = (currentValue: string) => {
    if (currentValue) {
      if (currentValue.indexOf("-") === currentValue.length - 1) {
        return false;
      }
    }
    return true;
  };

  const provinceOptions = getProvinceOptions(selectedCountry);

  const provinceValid = (value: string) => {
    if (value === null && !required) return true;
    return isProvinceValueValid(value);
  };

  return (
    <Col sm={6}>
      <Select
        name={name}
        label={t("Province")}
        key={definition.key}
        onChange={onChange}
        options={_.sortBy(provinceOptions, "label")}
        disabled={_.isEmpty(provinceOptions)}
        loading={loadingCountries}
        valid={provinceValid}
        required={required}
      />
    </Col>
  );
};

export default CountryProvinceSelector;
